/** eslint-disable */
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLocaleStore } from "@/store/locale";
import StemStudioService from "@/services/stemStudioService";
import { getApiError } from "@/utils/apiError";
import debounce from "lodash.debounce";

export function useMakerCartCatalog() {
  const route = useRoute();
  const router = useRouter();
  const localeStore = useLocaleStore();

  // State
  const searchQuery = ref("");
  const selectedSort = ref("top-activities");
  const activeLocale = ref(localeStore.currentLocale);
  const items = ref([]);
  const activeItems = ref([]);
  const isLoading = ref(true);
  const error = ref(null);
  const errorCode = ref(null);
  const currentPage = ref(1);
  const totalItems = ref(0);
  const totalPages = ref(0);
  const filterMode = ref("compact");
  const windowWidth = ref(window.innerWidth);
  const activeTab = ref("custom");
  const isInitializing = ref(true);
  const isSwitchingTabs = ref(false);

  // URL parameter keys for filters
  const URL_PARAM_KEYS = {
    subjects: "subjects",
    grades: "grades",
    focus: "focus",
    hardware: "hardware",
    duration: "duration",
    tags: "tags",
    sort: "sort",
    search: "search",
    page: "page",
    locale: "locale",
    popular: "popular",
    free: "free",
  };

  // Initial filter values from URL
  const initialFilters = ref({
    subjects: [],
    grades: [],
    focus: [],
    hardware: [],
    duration: [],
    tags: [],
    sort: "newest",
    locale: "US",
    popular: false,
    free: false,
  });

  // Computed properties
  const maxVisiblePages = 5;

  const visiblePages = computed(() => {
    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(currentPage.value - half, 1);
    let end = start + maxVisiblePages - 1;

    if (end > totalPages.value) {
      end = totalPages.value;
      start = Math.max(end - maxVisiblePages + 1, 1);
    }

    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  });

  const selectedChips = computed(() => {
    const chips = [];
    const ai = activeItems.value || {};

    const format = (key, label, allLabel) => {
      const list = ai[key] || [];
      if (!Array.isArray(list) || list.length === 0) return;
      const values = list.map((i) => i.title ?? i.text ?? i.value ?? i);
      const isAll =
        values.length > 1 &&
        (values.includes("all") ||
          values.includes(allLabel) ||
          values.some((v) => String(v).toLowerCase().startsWith("all ")));

      let text;
      if (isAll) {
        text = allLabel;
      } else {
        const filteredValues = values.filter(
          (v) => v !== "all" && v !== allLabel
        );
        if (filteredValues.length === 1) {
          text = filteredValues[0];
        } else {
          text = `<span class="chip-label">${label}:</span> <span class="chip-values" style="margin-left: 2px;"> ${filteredValues.join(" / ")}</span>`;
        }
      }
      chips.push({ key, text });
    };

    format("grades", "Grades", "All Grades");
    format("subjects", "Subjects", "All Subjects");
    format("focus", "Focus", "All Focus");
    format("hardwareAssociated", "Required Equipment", "All Hardware");
    format("duration", "Duration", "All Duration");

    const tagList = ai.tags || [];
    if (Array.isArray(tagList) && tagList.length > 0) {
      tagList.forEach((t) => {
        const title = t?.title ?? t?.value ?? t;
        const category = t?.category ?? "Tags";
        const text = `<span class="chip-label">${category}:</span> <span class="chip-values" style="margin-left: 2px;"> ${title}</span>`;
        chips.push({ key: `tag|${category}|${title}`, text });
      });
    }

    if (ai.popular) chips.push({ key: "popular", text: "Popular" });
    if (ai.free) chips.push({ key: "free", text: "Free" });

    return chips;
  });

  const emptyStateTitle = computed(() => {
    const hasSearch =
      searchQuery.value && String(searchQuery.value).trim() !== "";
    const hasFilters =
      activeItems.value &&
      ((activeItems.value.grades && activeItems.value.grades.length > 0) ||
        (activeItems.value.hardwareAssociated &&
          activeItems.value.hardwareAssociated.length > 0) ||
        (activeItems.value.tags && activeItems.value.tags.length > 0) ||
        (activeItems.value.prototypingMaterials &&
          activeItems.value.prototypingMaterials.length > 0) ||
        (activeItems.value.subjects && activeItems.value.subjects.length > 0) ||
        (activeItems.value.focus && activeItems.value.focus.length > 0));

    if (hasSearch && hasFilters) {
      return `No results found for '${searchQuery.value}' with current filters`;
    } else if (hasSearch) {
      return `Oh no! We didn't find any results for '${searchQuery.value}'`;
    } else if (hasFilters) {
      return "No results match your current filters";
    } else {
      return "No makercart activities available";
    }
  });

  const notFoundMessage = computed(() => {
    const hasSearch =
      searchQuery.value && String(searchQuery.value).trim() !== "";
    const hasFilters =
      activeItems.value &&
      ((activeItems.value.grades && activeItems.value.grades.length > 0) ||
        (activeItems.value.hardwareAssociated &&
          activeItems.value.hardwareAssociated.length > 0) ||
        (activeItems.value.tags && activeItems.value.tags.length > 0) ||
        (activeItems.value.prototypingMaterials &&
          activeItems.value.prototypingMaterials.length > 0) ||
        (activeItems.value.subjects && activeItems.value.subjects.length > 0) ||
        (activeItems.value.focus && activeItems.value.focus.length > 0));

    if (hasSearch && hasFilters) {
      return "Try adjusting your search terms or clearing some filters to see more results.";
    } else if (hasSearch) {
      return "Try different search terms or clear filters to see more results.";
    } else if (hasFilters) {
      return "No activities match your current filters. Try clearing some filters to see more results.";
    } else {
      return "No makercart activities are currently available. Please check back later.";
    }
  });

  const sidebarStyle = computed(() => {
    let compactWidth, expandedWidth;

    if (windowWidth.value > 1600) {
      compactWidth = "10%";
      expandedWidth = "18%";
    } else if (windowWidth.value > 768) {
      compactWidth = "15%";
      expandedWidth = "25%";
    } else {
      // Mobile: Hide sidebar completely or make it full width
      compactWidth = "0%";
      expandedWidth = "100%";
    }

    return filterMode.value === "expanded"
      ? { width: expandedWidth }
      : { width: compactWidth };
  });

  const mainContentStyle = computed(() => {
    let compactWidth, expandedWidth, compactPadding, expandedPadding;

    if (windowWidth.value > 1200) {
      compactWidth = "90%";
      expandedWidth = "82%";
      compactPadding = "0px 30px";
      expandedPadding = "0px 20px";
    } else if (windowWidth.value > 768) {
      compactWidth = "85%";
      expandedWidth = "75%";
      compactPadding = "0px 20px";
      expandedPadding = "0px 15px";
    } else {
      // Mobile: Full width with minimal padding
      compactWidth = "100%";
      expandedWidth = "100%";
      compactPadding = "0px 16px";
      expandedPadding = "0px 16px";
    }

    return filterMode.value === "expanded"
      ? { padding: expandedPadding, width: expandedWidth }
      : { padding: compactPadding, width: compactWidth };
  });

  // Methods
  const performSearch = () => {
    currentPage.value = 1;
    updateUrlWithFilters(
      activeItems.value,
      selectedSort.value,
      searchQuery.value,
      currentPage.value,
      activeLocale.value
    );
    getAll({ search: searchQuery.value });
  };

  const debouncedSearch = debounce(performSearch, 500);

  const handleSearch = () => {
    debouncedSearch();
  };

  const clearSearch = () => {
    searchQuery.value = "";
    currentPage.value = 1;
    updateUrlWithFilters(
      activeItems.value,
      selectedSort.value,
      searchQuery.value,
      currentPage.value,
      activeLocale.value
    );
    getAll({ search: searchQuery.value });
  };

  const changePage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page;
      updateUrlWithFilters(
        activeItems.value,
        selectedSort.value,
        searchQuery.value,
        currentPage.value,
        activeLocale.value
      );
    }
  };

  const sortChanged = (sortParam) => {
    selectedSort.value = sortParam;
    currentPage.value = 1;
    updateUrlWithFilters(
      activeItems.value,
      sortParam,
      searchQuery.value,
      currentPage.value,
      activeLocale.value
    );
    getAll({ search: searchQuery.value });
  };

  const localeChanged = (locale) => {
    localeStore.setLocale(locale);
    activeLocale.value = locale;
    currentPage.value = 1;
    updateUrlWithFilters(
      activeItems.value,
      selectedSort.value,
      searchQuery.value,
      currentPage.value,
      locale
    );
  };

  const clearFilter = (filterKey) => {
    const newActiveItems = { ...activeItems.value };

    if (filterKey === "popular") {
      newActiveItems.popular = false;
    } else if (filterKey === "free") {
      newActiveItems.free = false;
    } else if (filterKey.startsWith("tag|")) {
      const parts = filterKey.split("|");
      const category = parts[1];
      const title = parts[2];
      const list = Array.isArray(newActiveItems.tags)
        ? newActiveItems.tags
        : [];
      const idx = list.findIndex(
        (t) => (t?.title ?? t?.value ?? t) === title && t?.category === category
      );
      if (idx > -1) {
        list.splice(idx, 1);
        newActiveItems.tags = [...list];
      }
    } else {
      newActiveItems[filterKey] = [];
    }

    activeItems.value = newActiveItems;
    currentPage.value = 1;
    updateUrlWithFilters(
      newActiveItems,
      selectedSort.value,
      searchQuery.value,
      currentPage.value,
      activeLocale.value
    );
    setInitialFiltersFromActiveItems(newActiveItems);
    getAll({ search: searchQuery.value });
  };

  const clearFilterState = () => {
    activeItems.value = {};
    currentPage.value = 1;

    initialFilters.value = {
      subjects: [],
      grades: [],
      focus: [],
      hardware: [],
      duration: [],
      tags: [],
      sort: "newest",
      locale: activeLocale.value || "US",
      popular: false,
      free: false,
    };
  };

  const clearAllFilters = () => {
    clearFilterState();
    updateUrlWithFilters(
      {},
      selectedSort.value,
      searchQuery.value,
      currentPage.value,
      activeLocale.value
    );
    getAll({ search: searchQuery.value });
  };

  const filtersChanged = (dataFilter) => {
    activeItems.value = dataFilter;
    const wasPageChanged = currentPage.value !== 1;
    if (wasPageChanged) {
      currentPage.value = 1;
    }

    // Suppress auto-fetch and URL churn during the initial bootstrapping
    if (isInitializing.value) {
      return;
    }

    updateUrlWithFilters(
      dataFilter,
      selectedSort.value,
      searchQuery.value,
      1,
      activeLocale.value
    );
    if (!wasPageChanged) {
      getAll({ search: searchQuery.value });
    }
  };

  const onFilterModeChanged = (mode) => {
    filterMode.value = mode === "expanded" ? "expanded" : "compact";
  };

  const setActiveTab = (tab) => {
    // Set flag to prevent watchers from triggering during tab switch
    isSwitchingTabs.value = true;

    activeTab.value = tab;

    // Update URL with tab parameter only FIRST (clears all query params except tab)
    updateUrlWithTab(tab);

    // Then clear filter state to reset all filters
    clearFilterState();

    // Reset search and sort
    searchQuery.value = "";
    if (tab === "custom") {
      selectedSort.value = "top-activities";
    } else if (tab === "signature") {
      selectedSort.value = "newest";
    } else {
      selectedSort.value = "newest";
    }

    // Reset the flag after all state changes
    isSwitchingTabs.value = false;

    // Fetch only when switching to custom tab
    if (tab === "custom") {
      getAll();
    }
  };

  const updateUrlWithTab = (tab) => {
    // Only keep the tab parameter, clear all other query params
    router.replace({ query: { tab } });
  };

  const clickCartButton = () => {
    router.push({
      name: "MakerCartCartPage",
    });
  };

  const handleResize = () => {
    windowWidth.value = window.innerWidth;
  };

  // Parse URL parameters to extract filter values
  const parseFiltersFromUrl = () => {
    const query = route.query;

    const parseArrayParam = (param) => {
      if (!param) return [];
      return Array.isArray(param) ? param : param.split(",").filter(Boolean);
    };

    initialFilters.value = {
      subjects: parseArrayParam(query[URL_PARAM_KEYS.subjects]),
      grades: parseArrayParam(query[URL_PARAM_KEYS.grades]),
      focus: parseArrayParam(query[URL_PARAM_KEYS.focus]),
      hardware: parseArrayParam(query[URL_PARAM_KEYS.hardware]),
      duration: parseArrayParam(query[URL_PARAM_KEYS.duration]),
      tags: parseArrayParam(query[URL_PARAM_KEYS.tags]),
      sort: query[URL_PARAM_KEYS.sort] || "newest",
      locale: query[URL_PARAM_KEYS.locale] || "US",
      popular: query[URL_PARAM_KEYS.popular] === "true" || false,
      free: query[URL_PARAM_KEYS.free] === "true" || false,
    };

    if (query[URL_PARAM_KEYS.search]) {
      searchQuery.value = query[URL_PARAM_KEYS.search];
    }

    if (query[URL_PARAM_KEYS.page]) {
      currentPage.value = parseInt(query[URL_PARAM_KEYS.page]) || 1;
    }

    if (query[URL_PARAM_KEYS.sort]) {
      selectedSort.value = query[URL_PARAM_KEYS.sort];
    }

    if (query[URL_PARAM_KEYS.locale]) {
      activeLocale.value = query[URL_PARAM_KEYS.locale];
    }

    // Set active tab from URL
    if (query.tab) {
      activeTab.value = query.tab;
    } else {
      activeTab.value = "custom"; // Default to custom tab
    }
  };

  // Update URL with current filter values
  const updateUrlWithFilters = (
    filters,
    sortValue,
    searchValue,
    pageValue,
    localeValue
  ) => {
    const query = { ...route.query };

    // Always preserve the tab parameter
    if (activeTab.value) {
      query.tab = activeTab.value;
    }

    console.log("Query", query);

    const setArrayParam = (key, value) => {
      if (value && value.length > 0) {
        query[key] = value.join(",");
      } else {
        delete query[key];
      }
    };

    if (filters) {
      const normalize = (item) => (item && (item.value ?? item.title)) || item;
      setArrayParam(
        URL_PARAM_KEYS.subjects,
        filters.subjects?.map((item) => normalize(item))
      );
      setArrayParam(
        URL_PARAM_KEYS.grades,
        filters.grades?.map((item) => normalize(item))
      );
      setArrayParam(
        URL_PARAM_KEYS.focus,
        filters.focus?.map((item) => normalize(item))
      );
      setArrayParam(
        URL_PARAM_KEYS.hardware,
        filters.hardware?.map((item) => normalize(item))
      );
      setArrayParam(
        URL_PARAM_KEYS.duration,
        filters.duration?.map((item) => normalize(item))
      );
      setArrayParam(
        URL_PARAM_KEYS.tags,
        filters.tags?.map((item) => normalize(item))
      );

      if (filters.popular) {
        query[URL_PARAM_KEYS.popular] = filters.popular.toString();
      } else {
        delete query[URL_PARAM_KEYS.popular];
      }

      if (filters.free) {
        query[URL_PARAM_KEYS.free] = filters.free.toString();
      } else {
        delete query[URL_PARAM_KEYS.free];
      }
    }

    if (sortValue && sortValue !== "newest") {
      query[URL_PARAM_KEYS.sort] = sortValue;
    } else {
      delete query[URL_PARAM_KEYS.sort];
    }

    if (searchValue) {
      query[URL_PARAM_KEYS.search] = searchValue;
    } else {
      delete query[URL_PARAM_KEYS.search];
    }

    if (pageValue && pageValue > 1) {
      query[URL_PARAM_KEYS.page] = pageValue.toString();
    } else {
      delete query[URL_PARAM_KEYS.page];
    }

    if (localeValue && localeValue !== "US") {
      query[URL_PARAM_KEYS.locale] = localeValue;
    } else {
      delete query[URL_PARAM_KEYS.locale];
    }

    router.replace({ query });
  };

  const getAll = async (params = {}) => {
    try {
      isLoading.value = true;
      error.value = null;
      const filters = {};

      if (activeItems.value.grades && activeItems.value.grades.length > 0) {
        filters.grades = activeItems.value.grades
          .map((item) => item.title)
          .join(",");
      }

      if (
        activeItems.value.hardwareAssociated &&
        activeItems.value.hardwareAssociated.length > 0
      ) {
        filters.hardwareAssociated = activeItems.value.hardwareAssociated
          .map((item) => item.title)
          .join(",");
      }

      if (activeItems.value.tags && activeItems.value.tags.length > 0) {
        filters.tags = activeItems.value.tags
          .map((item) => item.title)
          .join(",");
      }

      if (
        activeItems.value.prototypingMaterials &&
        activeItems.value.prototypingMaterials.length > 0
      ) {
        filters.prototypingMaterials = activeItems.value.prototypingMaterials
          .map((item) => item.title)
          .join(",");
      }

      if (activeItems.value.subjects && activeItems.value.subjects.length > 0) {
        filters.subjects = activeItems.value.subjects
          .map((item) => item.title)
          .join(",");
      }

      if (activeItems.value.focus && activeItems.value.focus.length > 0) {
        filters.focus = activeItems.value.focus
          .map((item) => item.title)
          .join(",");
      }

      if (activeItems.value.duration && activeItems.value.duration.length > 0) {
        filters.duration = activeItems.value.duration
          .map((item) => item.value ?? item.title)
          .join(",");
      }

      if (activeLocale.value) {
        filters.locale = activeLocale.value;
      }

      if (activeItems.value.popular !== undefined) {
        filters.popular = activeItems.value.popular;
      }
      if (activeItems.value.free !== undefined) {
        filters.free = activeItems.value.free;
      }

      let sortParam;

      if (selectedSort.value === "newest") {
        sortParam = {
          sort_dir: "asc",
          sort_field: "Created",
        };
      } else if (selectedSort.value === "oldest") {
        sortParam = {
          sort_dir: "desc",
          sort_field: "Created",
        };
      } else if (selectedSort.value === "a-z") {
        sortParam = {
          sort_dir: "asc",
          sort_field: "Makercart Activity",
        };
      } else if (selectedSort.value === "z-a") {
        sortParam = {
          sort_dir: "desc",
          sort_field: "Makercart Activity",
        };
      } else if (selectedSort.value === "top-activities") {
        sortParam = {
          sort_dir: "desc",
          sort_field: "popular",
        };
      }

      const response = await StemStudioService.getAll({
        page: currentPage.value,
        limit: 9,
        ...sortParam,
        ...filters,
        ...(searchQuery.value ? { search: searchQuery.value } : {}),
        ...params,
      });

      if (response.status === 200) {
        items.value = response.data.data.rows.map((item) => ({
          ...item,
          image:
            item.image_thumbnail && item.image_thumbnail.length > 0
              ? item.image_thumbnail[0]
              : "",
        }));

        totalItems.value = response.data.data.total_rows;
        totalPages.value = response.data.data.total_pages;
      }
    } catch (err) {
      const { message, code } = getApiError(
        err,
        "Failed to fetch items. Please try again later."
      );
      error.value = message;
      errorCode.value = code ?? null;
    } finally {
      isLoading.value = false;
    }
  };

  const setInitialFiltersFromActiveItems = (ai) => {
    const mapArr = (arr) =>
      Array.isArray(arr) ? arr.map((i) => normalizeOptionValue(i)) : [];
    initialFilters.value = {
      subjects: mapArr(ai.subjects),
      grades: mapArr(ai.grades),
      focus: mapArr(ai.focus),
      hardware: mapArr(ai.hardwareAssociated),
      duration: mapArr(ai.duration),
      tags: mapArr(ai.tags),
      sort: selectedSort.value || "top-activities",
      locale: activeLocale.value || "US",
      popular: Boolean(ai.popular),
      free: Boolean(ai.free),
    };
  };

  const normalizeOptionValue = (item) => {
    return item?.value ?? item?.title ?? item;
  };

  watch(
    activeItems,
    () => {
      if (
        isInitializing.value &&
        activeTab.value === "custom" &&
        !isSwitchingTabs.value
      ) {
        console.log("Active items changed", activeItems.value);
        // Sync URL and fetch once
        updateUrlWithFilters(
          activeItems.value,
          selectedSort.value,
          searchQuery.value,
          currentPage.value,
          activeLocale.value
        );
        isInitializing.value = false;
      }
    },
    { deep: true }
  );

  // Watch for page changes (only for custom tab)
  watch([currentPage], () => {
    if (
      activeTab.value === "custom" &&
      !isInitializing.value &&
      !isSwitchingTabs.value
    ) {
      getAll({ search: searchQuery.value });
    }
  });

  const endInitialization = () => {
    isInitializing.value = false;
  };

  return {
    // State
    searchQuery,
    selectedSort,
    activeLocale,
    items,
    activeItems,
    isLoading,
    error,
    errorCode,
    currentPage,
    totalItems,
    totalPages,
    filterMode,
    windowWidth,
    activeTab,
    initialFilters,

    // Computed
    visiblePages,
    selectedChips,
    emptyStateTitle,
    notFoundMessage,
    sidebarStyle,
    mainContentStyle,

    // Methods
    handleSearch,
    clearSearch,
    changePage,
    sortChanged,
    localeChanged,
    clearFilter,
    clearAllFilters,
    filtersChanged,
    onFilterModeChanged,
    setActiveTab,
    clickCartButton,
    handleResize,
    parseFiltersFromUrl,
    getAll,
    setInitialFiltersFromActiveItems,
    endInitialization,
  };
}
